import { useParams, Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import SEOHead from '../../../components/base/SEOHead';
import { menuCategories, menuItems } from '../../../mocks/menuData';

const MenuCategoryPage = () => {
  const { category } = useParams<{ category: string }>();
  
  const categoryData = menuCategories.find(cat => cat.slug === category);
  const items = category ? menuItems[category as keyof typeof menuItems] || [] : [];

  if (!categoryData) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <main className="pt-20 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-slate-900 mb-4">Category Not Found</h1>
            <Link to="/menu" className="text-blue-600 hover:text-blue-700 font-semibold">
              ← Back to Menu
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Menu",
    "name": `${categoryData.name} - Ocean View Inn & Sports Pub`,
    "description": `Explore our ${categoryData.name.toLowerCase()} menu featuring fresh Florida Keys cuisine`,
    "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/menu/${category}`,
    "inLanguage": "en-US",
    "hasMenuSection": {
      "@type": "MenuSection",
      "name": categoryData.name,
      "hasMenuItem": categoryData.items.map(item => ({
        "@type": "MenuItem",
        "name": item.name,
        "description": item.description,
        "offers": {
          "@type": "Offer",
          "price": item.price,
          "priceCurrency": "USD"
        }
      }))
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title={`${categoryData.name} Menu Islamorada | Ocean View Inn & Sports Pub Florida Keys`}
        description={`Discover our ${categoryData.name.toLowerCase()} menu at Ocean View Inn & Sports Pub in Islamorada. Fresh Florida Keys cuisine, seafood, and classic American favorites. View our full ${categoryData.name.toLowerCase()} selection.`}
        keywords={`${categoryData.name} menu, Islamorada restaurant, Florida Keys dining, Ocean View Inn menu, ${categoryData.name.toLowerCase()}`}
        canonical={`${import.meta.env.VITE_SITE_URL || 'https://example.com'}/menu/${category}`}
        ogTitle={`${categoryData.name} Menu | Ocean View Inn Islamorada`}
        ogDescription={`Explore our ${categoryData.name.toLowerCase()} menu featuring fresh Florida Keys cuisine at Ocean View Inn & Sports Pub`}
        ogImage="https://public.readdy.ai/ai/img_res/1cb623544f7f1529cd712fc379ec741d.jpg"
        ogUrl={`${import.meta.env.VITE_SITE_URL || 'https://example.com'}/menu/${category}`}
        structuredData={structuredData}
      />
      <Header />
      <main className="pt-20">
        <section className="relative h-[300px] flex items-center justify-center overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center bg-top"
            style={{
              backgroundImage: `url(${categoryData.image})`,
            }}
          >
            <div className="absolute inset-0 bg-slate-900/70"></div>
          </div>
          <div className="relative z-10 text-center text-white max-w-4xl mx-auto px-4">
            <nav className="mb-4">
              <Link to="/menu" className="text-slate-300 hover:text-white transition-colors">
                Menu
              </Link>
              <span className="mx-2 text-slate-400">/</span>
              <span className="text-white">{categoryData.name}</span>
            </nav>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 font-serif">{categoryData.name}</h1>
            <p className="text-lg text-slate-200">{categoryData.description}</p>
          </div>
        </section>

        <section className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            {items.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {items.map((item, index) => (
                  <Link
                    key={index}
                    to={`/menu/${category}/${item.slug}`}
                    className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300 group"
                  >
                    <div className="aspect-w-16 aspect-h-9 overflow-hidden">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-full h-48 object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-6">
                      <div className="flex justify-between items-start mb-3">
                        <h3 className="text-xl font-bold text-slate-900 group-hover:text-blue-600 transition-colors">
                          {item.name}
                        </h3>
                        {item.price > 0 && (
                          <span className="text-lg font-bold text-blue-600">
                            ${item.price.toFixed(2)}
                          </span>
                        )}
                      </div>
                      <p className="text-slate-600 mb-4 line-clamp-3">{item.description}</p>
                      <div className="flex items-center text-blue-600 font-semibold group-hover:translate-x-2 transition-transform duration-300">
                        <span>View Details</span>
                        <i className="ri-arrow-right-line ml-2"></i>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <i className="ri-restaurant-line text-6xl text-slate-300 mb-4"></i>
                <h3 className="text-2xl font-bold text-slate-900 mb-2">Coming Soon</h3>
                <p className="text-slate-600">Menu items for this category will be available soon.</p>
              </div>
            )}

            <div className="mt-16 text-center">
              <Link
                to="/menu"
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-lg font-semibold hover:shadow-xl transform hover:scale-105 transition-all duration-300 whitespace-nowrap"
              >
                <i className="ri-arrow-left-line mr-2"></i>
                Back to All Categories
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default MenuCategoryPage;
